import { Spinner } from '@kcftech/design-system';
import { GroupType, ITreeItemResponse } from '@kcftech/prometheus-client';
import { useFeatureFlag } from 'contexts/configcat/useFeatureFlag';
import { useNotificationContext } from 'contexts/NotificationContext';
import { useRouteEntities } from 'contexts/RouteEntityContext';
import { useSystemsInfiniteDataSource } from 'custom_hooks/data_hooks/data_source/systemsDataSource';
import { useDrainDataSource } from 'custom_hooks/data_hooks/data_source/useDrainDataSource';
import { useTreeItems } from 'custom_hooks/data_hooks/tree/useTreeItems';
import { useTreeItemDataLoader } from 'pages/workbench/side_panel/workbench_tree/modals/useTreeItemDataLoader';
import {
    TreeItemGroupLabel,
    TreeItemGroupLabelText,
} from 'pages/workbench/side_panel/workbench_tree/styled';
import { TreeItemGroupIcon } from 'pages/workbench/side_panel/workbench_tree/TreeItemGroupIcon';
import { useEffect, useMemo, useRef, useState } from 'react';
import { DataTree } from 'shared/data_tree/DataTree';
import { createTree } from 'shared/data_tree/tree';
import { TreeExpansionConfig, TreeSelectionConfig } from 'shared/data_tree/types';
import styled from 'styled-components';
import { removeAllItems, toggleArrayItem } from 'utils/array';

const TreeItemLabelText = styled(TreeItemGroupLabelText)<{ $isRoot: boolean }>`
    font-size: 1.4rem;
`;

export type LocationsTreeProps = Readonly<{
    expandedGroupIds: string[];
    selectedGroupIds: string[];
    onExpandedChange: (groupIds: string[]) => void;
    onSelectedChange: (groupIds: string[]) => void;
    hideIndicatorGroups?: boolean;
    rootGroupIds?: string[];
}>;

export function LocationsTree({
    expandedGroupIds,
    selectedGroupIds,
    onExpandedChange,
    onSelectedChange,
    hideIndicatorGroups = false,
    rootGroupIds,
}: LocationsTreeProps) {
    const { value: timeSeriesCutoverPhase2 } = useFeatureFlag('timeSeriesCutoverPhase2');
    const shouldHideIndicatorGroups = timeSeriesCutoverPhase2 || hideIndicatorGroups;

    const { showToast } = useNotificationContext();
    const { loadingIds, loadChildren } = useTreeItemDataLoader();
    const { location, isLoading: isLoadingLocation } = useRouteEntities();

    const { results: systems, isLoading: isLoadingSystemsData } = useDrainDataSource(
        useSystemsInfiniteDataSource({
            defaultFilter: {
                locationId: location?.Id,
                isCollectorDependentSystem: false,
            },
            defaultSort: [{ field: 'Name', dir: 'asc' }],
            name: 'get-systems-from-location',
            enabled: !rootGroupIds,
        }),
    );

    const [tree, setTree] = useState(() => createTree<ITreeItemResponse>());

    const onExpandedChangeRef = useRef(onExpandedChange);
    onExpandedChangeRef.current = onExpandedChange;

    const expandedGroupIdsRef = useRef(expandedGroupIds);
    expandedGroupIdsRef.current = expandedGroupIds;

    const onSelectedChangeRef = useRef(onSelectedChange);
    onSelectedChangeRef.current = onSelectedChange;

    const rootItemIds = useMemo(
        () => rootGroupIds ?? systems?.map(({ Id }) => Id),
        [rootGroupIds, systems],
    );

    const {
        data: rootTreeItems,
        isLoading: isLoadingRootTreeItems,
        isFetching: isFetchingRootTreeItems,
    } = useTreeItems(rootItemIds);

    const expandable = useMemo<TreeExpansionConfig<ITreeItemResponse>>(
        () => ({
            isLoading: ({ data: treeItem }) => loadingIds.includes(treeItem.Id),
            isExpanded: ({ data: treeItem }) => expandedGroupIds.includes(treeItem.Id),
            canExpand: ({ data: treeItem }) =>
                treeItem.HasChildren &&
                (!shouldHideIndicatorGroups ||
                    treeItem.TreeItemType !== GroupType.MonitoringPoint),
            onChange: ({ node: { data: treeItem } }) =>
                onExpandedChangeRef.current(
                    toggleArrayItem(expandedGroupIds, treeItem.Id),
                ),
            onLoadChildren: ({ data: treeItem }) => {
                loadChildren({
                    parentIds: [treeItem.Id],
                    onSuccess: (parentId, children) => {
                        setTree((current) =>
                            current.replaceChildren({
                                id: parentId,
                                childData: children.map((data) => ({
                                    id: data.Id,
                                    data,
                                })),
                            }),
                        );
                    },
                    onError: (parentId) => {
                        showToast({ type: 'error', content: 'Unable to load children.' });
                        onExpandedChangeRef.current(
                            removeAllItems(expandedGroupIdsRef.current, [parentId]),
                        );
                    },
                });
            },
            indentNonExpandableRoots: false,
        }),
        [
            expandedGroupIds,
            loadChildren,
            loadingIds,
            showToast,
            shouldHideIndicatorGroups,
        ],
    );

    const selectable = useMemo<TreeSelectionConfig<ITreeItemResponse>>(
        () => ({
            selectionStyle: 'checkbox',
            isDisabled: ({ hierarchy }) =>
                hierarchy.some(({ id }) => selectedGroupIds.includes(id)),
            isSelected: ({ id, hierarchy }) =>
                selectedGroupIds.includes(id) ||
                hierarchy.some((child) => selectedGroupIds.includes(child.id)),
            onChange: ({ node, isSelected }) => {
                if (isSelected) {
                    const parentIds = node.hierarchy.map(({ id }) => id);
                    const childIds = node.allChildren.map(({ id }) => id);
                    const idsToDeselect = [...parentIds, ...childIds];

                    onSelectedChangeRef.current([
                        ...removeAllItems(selectedGroupIds, idsToDeselect),
                        node.id,
                    ]);
                } else {
                    onSelectedChangeRef.current(
                        removeAllItems(selectedGroupIds, [node.id]),
                    );
                }
            },
        }),
        [selectedGroupIds],
    );

    const isLoading =
        isLoadingLocation ||
        (!rootGroupIds && isLoadingSystemsData) ||
        isLoadingRootTreeItems ||
        isFetchingRootTreeItems;

    useEffect(() => {
        if (rootTreeItems) {
            setTree(() =>
                createTree<ITreeItemResponse>(
                    rootTreeItems.map((rootTreeItem) => ({
                        id: rootTreeItem.Id,
                        data: rootTreeItem,
                    })),
                ),
            );
        }
    }, [rootTreeItems]);

    useEffect(() => {
        const parentIds = tree.allNodes
            .filter(
                (node) =>
                    expandable.isExpanded(node) &&
                    !node.children &&
                    !loadingIds.includes(node.data.Id),
            )
            .map((node) => node.data.Id);

        if (parentIds.length > 0) {
            loadChildren({
                parentIds,
                onSuccess: (parentId, children) => {
                    setTree((current) =>
                        current.replaceChildren({
                            id: parentId,
                            childData: children.map((data) => ({
                                id: data.Id,
                                data,
                            })),
                        }),
                    );
                },
                onError: (parentId) => {
                    showToast({ type: 'error', content: 'Unable to load children.' });
                    onExpandedChangeRef.current(
                        removeAllItems(expandedGroupIdsRef.current, [parentId]),
                    );
                },
            });
        }
    }, [expandable, loadChildren, loadingIds, showToast, tree.allNodes]);

    return isLoading ? (
        <Spinner size="1x" />
    ) : (
        <DataTree<ITreeItemResponse>
            treeId="issue-locations-tree"
            data={tree}
            selectable={selectable}
            expandable={expandable}
            renderNode={({
                node: { isRoot, data: treeItem },
                isExpanded,
                isSelectable,
            }) => (
                <TreeItemGroupLabel $isDisabled={!isSelectable}>
                    <TreeItemGroupIcon
                        treeItemType={treeItem.TreeItemType}
                        isExpanded={isExpanded}
                    />
                    <TreeItemLabelText $isRoot={isRoot}>
                        {treeItem.Name}
                    </TreeItemLabelText>
                </TreeItemGroupLabel>
            )}
        />
    );
}
